import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, PiggyBank, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/a9d76516-0ad3-438a-9582-5bb8b54b8dd4/images/48ab9094258966307da05c9227a08be95fa6b689d5e5ea5f2c46b3e5ec2e92ae.jpeg";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0910] relative" data-testid="landing-page">
      <header className="sticky top-0 z-40 bg-[#0A0910]/80 backdrop-blur-xl border-b border-[#262336]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-baseline">
            <span className="font-display text-2xl font-black lowercase tracking-tight text-white">walker</span>
          </div>
          <button
            data-testid="header-login-button"
            onClick={login}
            className="grad-btn px-6 py-2.5 rounded-full text-white text-sm font-bold"
          >
            Sign in with Google
          </button>
        </div>
      </header>

      <main className="relative">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 pt-20 pb-28 items-center">
          <div className="rise">
        <p className="font-mono-fin text-[11px] font-semibold uppercase tracking-[0.28em] text-[#FF6B35] mb-8">
              Real valuations, real numbers
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.0] tracking-tight text-white">
              Know what your business is
              <span className="grad-text"> actually </span>
              worth.
            </h1>
            <p className="mt-8 text-base leading-relaxed text-[#B4ADC6] max-w-xl">
              walker gives you a real, defensible valuation of your business in three minutes. No
              advisers, no invoices, and a fresh number every single week.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <button
                data-testid="hero-login-button"
                onClick={login}
                className="grad-btn group inline-flex items-center gap-3 px-9 py-4 rounded-full text-white font-bold tracking-tight"
              >
                Value my business
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <span className="font-mono-fin text-xs text-[#9A93AD]">Free · takes 3 minutes</span>
            </div>

            <div className="mt-20 grid sm:grid-cols-3 gap-8 border-t border-[#262336] pt-10">
              {[
                {
                  icon: BadgeCheck,
                  t: "Get real valuations",
                  d: "A genuine buyer grade valuation of your business, built the way an acquirer would price it.",
                },
                {
                  icon: PiggyBank,
                  t: "Skip the thousands in fees",
                  d: "Get your valuation without spending thousands of pounds on accountants or brokers.",
                },
                {
                  icon: RefreshCw,
                  t: "Live weekly updates",
                  d: "Valuation software that refreshes your number every single week as your business moves.",
                },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t}>
                  <Icon size={20} className="text-[#FF6B35]" strokeWidth={2} />
                  <p className="mt-4 font-display text-lg font-bold text-white">{t}</p>
                  <p className="mt-2 text-sm text-[#9A93AD] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative rise" style={{ animationDelay: "120ms" }}>
            <div className="grad-ring relative overflow-hidden rounded-[28px]">
              <img src={HERO} alt="Abstract gradient light ribbon" className="w-full h-[560px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0910] via-[#0A0910]/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="grad-ring bg-[#141220]/95 backdrop-blur-xl rounded-[22px] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
                  <p className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[#9A93AD]">
                    Sample output
                  </p>
                  <p className="mt-4 font-display text-4xl font-black tracking-tight grad-text whitespace-nowrap">$450k to $650k</p>
                  <p className="mt-3 font-mono-fin text-xs font-semibold text-[#FFC93C]">4.5x adjusted multiple</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
